// SmartTable: drop-in replacement for MUI <Table> that gives every SFM
// table the same column controls:
//   * Columns: hide / show any column (saved per user, per table)
//   * Resize: drag the right edge of a column header (saved per user)
//   * Filter: type into a per-column box to narrow the rows on screen
//
// View-only. SmartTable never changes, deletes, or re-orders records.
// Filters are deliberately NOT saved, and an active filter always shows
// "Showing X of Y rows" so nobody mistakes a filtered view for the record.
// Printing always shows every column and row (controls are screen only).
//
// Usage: replace `<Table ...>` with `<SmartTable ...>` (same props).
// Optional `tableId` gives a stable storage key (one setting for every job).
// `minWidths` ({ "Action": 300 }) is a floor no saved or dragged width can go below.
// `lockedColumns` (header text) can never be hidden: use it for the columns
// a controlled record must always show (e.g. drawing Rev and Status).
// Tables with merged header cells or several header rows are left as plain
// tables, with no controls, so columns can never misalign.

import React, { forwardRef, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Table, Box, Button, Menu, MenuItem, Checkbox, ListItemText, TextField, Typography, Divider,
} from "@mui/material";
import {
  ViewColumnOutlined, FilterListOutlined, RestartAltOutlined,
} from "@mui/icons-material";
import {
  headerLabels, storageKey, loadPrefs, savePrefs, buildCss, rowMatches, MIN_WIDTH,
  headerIsSimple, effectiveHidden,
} from "./smartTableCore";

let seq = 0;
const EDGE = 8; // px from a header's right edge that starts a resize
const raf = (cb) => (typeof window !== "undefined" && window.requestAnimationFrame
  ? window.requestAnimationFrame(cb) : setTimeout(cb, 0));
const caf = (id) => (typeof window !== "undefined" && window.cancelAnimationFrame
  ? window.cancelAnimationFrame(id) : clearTimeout(id));

const SmartTable = forwardRef(function SmartTable({ tableId, lockedColumns, minWidths, className, children, ...rest }, ref) {
  const tableRef = useRef(null);
  const scopeRef = useRef(null);
  if (!scopeRef.current) { seq += 1; scopeRef.current = `sfm-st-${seq}`; }
  const scope = scopeRef.current;

  const [labels, setLabels] = useState([]);
  const [simple, setSimple] = useState(true);
  const [prefs, setPrefs] = useState({ hidden: [], widths: {} });
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [menuEl, setMenuEl] = useState(null);
  const [counts, setCounts] = useState({ shown: 0, total: 0 });
  const keyRef = useRef(null);
  const labelsRef = useRef([]);
  labelsRef.current = labels;
  const minWidthsRef = useRef(minWidths);
  minWidthsRef.current = minWidths;
  const simpleRef = useRef(true);
  simpleRef.current = simple;
  const path = typeof window !== "undefined" ? window.location.pathname : "";

  const setRefs = useCallback((el) => {
    tableRef.current = el;
    if (typeof ref === "function") ref(el);
    else if (ref) ref.current = el;
  }, [ref]);

  const readLabels = useCallback(() => {
    const next = headerLabels(tableRef.current);
    const ok = headerIsSimple(tableRef.current);
    setSimple((prev) => (prev === ok ? prev : ok));
    setLabels((prev) => (prev.join("\u0001") === next.join("\u0001") ? prev : next));
  }, []);

  useLayoutEffect(() => { readLabels(); }, [readLabels]);

  useEffect(() => {
    if (!labels.length) return;
    const key = storageKey(path, labels, tableId);
    if (keyRef.current !== key) {
      keyRef.current = key;
      setPrefs(loadPrefs(key));
    }
  }, [labels, path, tableId]);

  const updatePrefs = useCallback((fn, persist = true) => {
    setPrefs((p) => {
      const n = fn(p);
      if (persist && keyRef.current) savePrefs(keyRef.current, n);
      return n;
    });
  }, []);

  const lockKey = (lockedColumns || []).join("\u0001");
  const minKey = JSON.stringify(minWidths || {});
  const locked = useMemo(() => new Set(lockedColumns || []), [lockKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const hiddenList = useMemo(
    () => (simple ? effectiveHidden(prefs.hidden, lockedColumns) : []),
    [simple, prefs.hidden, lockKey], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const hiddenSet = useMemo(() => new Set(hiddenList), [hiddenList]);
  const visibleLabels = labels.filter((l) => !hiddenSet.has(l));
  const activeFilters = visibleLabels.filter((l) => (filters[l] || "").trim()).length;

  // Hide rows that do not match the active filters (data rows only).
  const applyFilters = useCallback(() => {
    const table = tableRef.current;
    if (!table) return;
    if (!simple) {
      table.querySelectorAll(":scope > tbody > tr[data-st-hide]").forEach((tr) => tr.removeAttribute("data-st-hide"));
      return;
    }
    const byIndex = {};
    labels.forEach((l, i) => {
      if (!hiddenSet.has(l) && (filters[l] || "").trim()) byIndex[i] = filters[l];
    });
    const active = Object.keys(byIndex).length > 0;
    let shown = 0;
    let total = 0;
    table.querySelectorAll(":scope > tbody > tr").forEach((tr) => {
      const cells = tr.children;
      const isData = cells.length === labels.length && !tr.querySelector(":scope > td[colspan]");
      const ok = !isData || !active
        || rowMatches(Array.from(cells).map((c) => c.textContent || ""), byIndex);
      if (isData) { total += 1; if (ok) shown += 1; }
      if (ok && tr.hasAttribute("data-st-hide")) tr.removeAttribute("data-st-hide");
      if (!ok && !tr.hasAttribute("data-st-hide")) tr.setAttribute("data-st-hide", "");
    });
    setCounts((c) => (c.shown === shown && c.total === total ? c : { shown, total }));
  }, [labels, filters, hiddenSet, simple]);

  // Re-read headers and re-apply filters whenever the table content changes.
  useEffect(() => {
    const table = tableRef.current;
    if (!table) return undefined;
    let id = 0;
    const run = () => { caf(id); id = raf(() => { readLabels(); applyFilters(); }); };
    run();
    if (typeof MutationObserver === "undefined") return () => caf(id);
    const mo = new MutationObserver(run);
    mo.observe(table, { childList: true, subtree: true, characterData: true });
    return () => { caf(id); mo.disconnect(); };
  }, [readLabels, applyFilters]);

  // Column resizing: drag within EDGE px of a header cell's right edge.
  useEffect(() => {
    const table = tableRef.current;
    if (!table) return undefined;
    const headerCells = () => {
      const rows = table.querySelectorAll(":scope > thead > tr");
      return rows.length ? Array.from(rows[rows.length - 1].children) : [];
    };
    const edgeTh = (e) => {
      const th = e.target && e.target.closest ? e.target.closest("th") : null;
      if (!th || th.closest("table") !== table || !headerCells().includes(th)) return null;
      const r = th.getBoundingClientRect();
      return e.clientX >= r.right - EDGE && e.clientX <= r.right + 2 ? th : null;
    };
    let drag = null;
    let justDragged = false;
    const onMove = (e) => {
      if (drag) {
        const floor = (minWidthsRef.current && minWidthsRef.current[drag.label]) || 0;
        const w = Math.max(MIN_WIDTH, floor, Math.round(drag.start + (e.clientX - drag.x)));
        updatePrefs((p) => ({ ...p, widths: { ...p.widths, [drag.label]: w } }), false);
        return;
      }
      if (simpleRef.current && edgeTh(e)) table.setAttribute("data-st-resize", "");
      else table.removeAttribute("data-st-resize");
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      if (drag) { justDragged = true; setTimeout(() => { justDragged = false; }, 0); }
      drag = null;
      updatePrefs((p) => p, true);
    };
    const onDown = (e) => {
      if (e.button !== 0 || !simpleRef.current) return;
      const th = edgeTh(e);
      if (!th) return;
      e.preventDefault();
      e.stopPropagation();
      const ths = headerCells();
      const label = labelsRef.current[ths.indexOf(th)];
      if (!label) return;
      updatePrefs((p) => {
        const widths = { ...p.widths };
        ths.forEach((c, i) => {
          const l = labelsRef.current[i];
          const w = c.getBoundingClientRect().width;
          if (l && !widths[l] && w > 0) widths[l] = Math.round(w);
        });
        return { ...p, widths };
      }, false);
      drag = { label, x: e.clientX, start: th.getBoundingClientRect().width };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    };
    const onClickCapture = (e) => { if (justDragged) { e.stopPropagation(); e.preventDefault(); } };
    table.addEventListener("pointerdown", onDown, true);
    table.addEventListener("pointermove", onMove);
    table.addEventListener("click", onClickCapture, true);
    return () => {
      table.removeEventListener("pointerdown", onDown, true);
      table.removeEventListener("pointermove", onMove);
      table.removeEventListener("click", onClickCapture, true);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [updatePrefs]);

  const css = useMemo(
    () => (simple
      ? `${buildCss(scope, labels, { ...prefs, hidden: hiddenList }, minWidths)}\ntable.${scope}[data-st-resize] > thead{cursor:col-resize;}`
      : ""),
    [scope, labels, prefs, hiddenList, simple, minKey], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const toggle = (label) => !locked.has(label) && updatePrefs((p) => {
    const hidden = p.hidden.includes(label) ? p.hidden.filter((h) => h !== label) : [...p.hidden, label];
    return { ...p, hidden };
  });
  const showAll = () => { updatePrefs((p) => ({ ...p, hidden: [] })); setMenuEl(null); };
  const reset = () => { setFilters({}); updatePrefs(() => ({ hidden: [], widths: {} })); };
  const customized = hiddenList.length > 0 || Object.keys(prefs.widths).length > 0 || activeFilters > 0;

  return (
    <>
      <style>{css}</style>
      {simple && labels.length > 1 && (
        <Box
          data-testid="smart-table-toolbar"
          sx={{
            "@media print": { display: "none" },
            position: "sticky", left: 0, zIndex: 3, display: "flex", flexWrap: "wrap",
            alignItems: "center", gap: 1, px: 1, py: 0.5, bgcolor: "background.paper",
            borderBottom: 1, borderColor: "divider",
          }}
        >
          <Button size="small" color={hiddenList.length ? "warning" : "primary"} startIcon={<ViewColumnOutlined />} onClick={(e) => setMenuEl(e.currentTarget)}>
            Columns{hiddenList.length ? ` (${hiddenList.length} hidden)` : ""}
          </Button>
          <Button
            size="small"
            color={activeFilters ? "warning" : "primary"}
            startIcon={<FilterListOutlined />}
            onClick={() => setShowFilters((s) => !s)}
            aria-expanded={showFilters}
          >
            Filter{activeFilters ? ` (${activeFilters})` : ""}
          </Button>
          {customized && (
            <Button size="small" color="inherit" startIcon={<RestartAltOutlined />} onClick={reset}>
              Reset view
            </Button>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: { xs: "none", md: "inline" } }}>
            Drag a column edge to resize.
          </Typography>
          {activeFilters > 0 && (
            <Typography variant="caption" color="warning.main" sx={{ ml: "auto", fontWeight: 600 }}>
              Filtered: showing {counts.shown} of {counts.total} rows on this page
            </Typography>
          )}
        </Box>
      )}
      {simple && showFilters && labels.length > 1 && (
        <Box
          sx={{
            "@media print": { display: "none" },
            position: "sticky", left: 0, zIndex: 3, display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 1, p: 1,
            bgcolor: "background.paper", borderBottom: 1, borderColor: "divider",
          }}
        >
          {visibleLabels.map((l) => (
            <TextField
              key={l}
              size="small"
              label={l}
              placeholder="Contains..."
              value={filters[l] || ""}
              onChange={(e) => setFilters((f) => ({ ...f, [l]: e.target.value }))}
            />
          ))}
          {activeFilters > 0 && (
            <Button size="small" onClick={() => setFilters({})}>Clear filters</Button>
          )}
        </Box>
      )}
      <Table ref={setRefs} className={[className, scope].filter(Boolean).join(" ")} {...rest}>
        {children}
      </Table>
      <Menu anchorEl={menuEl} open={Boolean(menuEl)} onClose={() => setMenuEl(null)}>
        {labels.map((l) => {
          const on = !hiddenSet.has(l);
          const isLocked = locked.has(l);
          const lastVisible = on && visibleLabels.length === 1;
          return (
            <MenuItem key={l} dense disabled={lastVisible || isLocked} onClick={() => toggle(l)}>
              <Checkbox size="small" checked={on} sx={{ p: 0.5, mr: 1 }} />
              <ListItemText primary={l} secondary={isLocked ? "Always shown" : null} />
            </MenuItem>
          );
        })}
        <Divider />
        <MenuItem dense onClick={showAll}>Show all columns</MenuItem>
      </Menu>
    </>
  );
});

export default SmartTable;
